import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Globe,
  MapPin,
  Monitor,
  Clock,
  Shield,
  Tag,
  MessageSquare,
  Plus,
  X
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [newTag, setNewTag] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchLog = async () => {
    try {
      const response = await axios.get(`${API}/logs/${id}`, {
        headers: getAuthHeader()
      });
      setLog(response.data);
    } catch (error) {
      toast.error("Failed to fetch log details");
      navigate("/logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
  }, [id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/logs/${id}/notes`, 
        { note: newNote },
        { headers: getAuthHeader() }
      );
      toast.success("Note added");
      setNewNote("");
      fetchLog();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;
    
    const updatedTags = [...(log.tags || []), newTag.trim()];
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/logs/${id}/tags`, 
        { tags: updatedTags },
        { headers: getAuthHeader() }
      );
      toast.success("Tag added");
      setNewTag("");
      fetchLog();
    } catch (error) {
      toast.error("Failed to add tag");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    const updatedTags = (log.tags || []).filter(t => t !== tagToRemove);
    
    try {
      await axios.post(`${API}/logs/${id}/tags`, 
        { tags: updatedTags },
        { headers: getAuthHeader() }
      );
      toast.success("Tag removed");
      fetchLog();
    } catch (error) {
      toast.error("Failed to remove tag");
    }
  };

  const handleBlacklist = async () => {
    try {
      await axios.post(`${API}/blacklist`, 
        { ip_address: log.ip_address, reason: "Manually blacklisted" },
        { headers: getAuthHeader() }
      );
      toast.success("IP blacklisted");
      fetchLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to blacklist");
    }
  };

  const handleWhitelist = async () => {
    try {
      await axios.post(`${API}/whitelist`, 
        { ip_address: log.ip_address, reason: "Manually whitelisted" },
        { headers: getAuthHeader() }
      );
      toast.success("IP whitelisted");
      fetchLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to whitelist");
    }
  };

  const getThreatBadge = (level) => {
    const variants = {
      safe: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      suspicious: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      threat: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return variants[level] || variants.safe;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!log) return null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="log-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/logs")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight text-primary">
            {log.ip_address}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Logged on {new Date(log.timestamp).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-sm uppercase ${getThreatBadge(log.threat_level)}`}>
            {log.threat_level}
          </Badge>
          {log.is_blacklisted && (
            <Badge variant="destructive">Blacklisted</Badge>
          )}
          {log.is_whitelisted && (
            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Whitelisted</Badge>
          )}
          {log.is_vpn && (
            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
              VPN {log.detection_confidence === "high" ? "(95%)" : "(~30%)"}
            </Badge>
          )}
          {log.is_proxy && (
            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
              Proxy {log.detection_confidence === "high" ? "(95%)" : "(~30%)"}
            </Badge>
          )}
          {log.is_tor && (
            <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Tor</Badge>
          )}
          {log.is_bot && (
            <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Bot</Badge>
          )}
          {log.is_datacenter && (
            <Badge variant="outline">Datacenter</Badge>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Location Info */}
        <Card className="bg-card/50 border-border" data-testid="location-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Location Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Country</p>
                <p className="font-medium mt-1">{log.country || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">City</p>
                <p className="font-medium mt-1">{log.city || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Region</p>
                <p className="font-medium mt-1">{log.region || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Timezone</p>
                <p className="font-medium mt-1 font-mono text-sm">{log.timezone || "Unknown"}</p>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">ISP</p>
              <p className="font-medium mt-1">{log.isp || "Unknown"}</p>
            </div>
            {log.latitude && log.longitude && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Coordinates</p>
                <p className="font-mono text-sm mt-1">{log.latitude}, {log.longitude}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device Info */}
        <Card className="bg-card/50 border-border" data-testid="device-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Monitor className="w-4 h-4 text-primary" />
              Device Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Browser</p>
                <p className="font-medium mt-1">{log.browser || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">OS</p>
                <p className="font-medium mt-1">{log.os || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Device Type</p>
                <p className="font-medium mt-1">{log.device || "Unknown"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Path</p>
                <p className="font-mono text-sm mt-1">{log.path || "/"}</p>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">User Agent</p>
              <p className="font-mono text-xs bg-background p-3 rounded border border-border break-all">
                {log.user_agent || "Not available"}
              </p>
            </div>
            {log.referer && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Referer</p>
                <p className="font-mono text-sm mt-1 truncate">{log.referer}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tags */}
        <Card className="bg-card/50 border-border" data-testid="tags-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" />
              Tags
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {(log.tags || []).length > 0 ? (
                log.tags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="gap-1 pr-1">
                    {tag}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 hover:bg-destructive/20"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No tags added</p>
              )}
            </div>
            <form onSubmit={handleAddTag} className="flex gap-2">
              <Input
                placeholder="Add a tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                className="flex-1 h-9 bg-background"
                data-testid="tag-input"
              />
              <Button type="submit" size="sm" disabled={submitting} data-testid="add-tag-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="bg-card/50 border-border" data-testid="actions-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20"
              onClick={handleBlacklist}
              disabled={log.is_blacklisted}
              data-testid="blacklist-btn"
            >
              <Shield className="w-4 h-4" />
              {log.is_blacklisted ? "Already Blacklisted" : "Add to Blacklist"}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 hover:bg-emerald-500/10 hover:text-emerald-500 hover:border-emerald-500/20"
              onClick={handleWhitelist}
              disabled={log.is_whitelisted}
              data-testid="whitelist-btn"
            >
              <Shield className="w-4 h-4" />
              {log.is_whitelisted ? "Already Whitelisted" : "Add to Whitelist"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card className="bg-card/50 border-border" data-testid="notes-card">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(log.notes || []).length > 0 ? (
            <div className="space-y-2">
              {log.notes.map((note, index) => (
                <div key={index} className="p-3 bg-background rounded border border-border">
                  <p className="text-sm">{note}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No notes added</p>
          )}
          <form onSubmit={handleAddNote} className="space-y-3">
            <Textarea
              placeholder="Add a note about this IP..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="bg-background min-h-[80px]"
              data-testid="note-input"
            />
            <Button type="submit" size="sm" disabled={submitting} data-testid="add-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogDetailPage;
